import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';

type View = 'login' | 'register' | 'forgot';

const LoginPage = () => {
  const { signIn, signUp, signInWithGoogle, resetPassword, user, loading, hasRole } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [view, setView] = useState<View>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && user) {
      const isAdmin = hasRole('admin');
      const isTeamleiter = hasRole('teamleiter');
      const isOffice = hasRole('office');
      const isMonteur = hasRole('monteur');
      const hasAdminAccess = isAdmin || isTeamleiter || isOffice;
      if (hasAdminAccess && isMonteur) {
        navigate('/role-select', { replace: true });
      } else if (hasAdminAccess) {
        navigate('/admin', { replace: true });
      } else {
        navigate('/vehicles', { replace: true });
      }
    }
  }, [loading, user, hasRole, navigate]);

  if (!loading && user) {
    return null;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const result = await signIn(email, password);
    setSubmitting(false);
    if (result.error) {
      toast({ title: 'Fehler', description: result.error, variant: 'destructive' });
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const result = await signUp(email, password, name);
    setSubmitting(false);
    if (result.error) {
      toast({ title: 'Fehler', description: result.error, variant: 'destructive' });
    } else {
      toast({ title: 'Registrierung erfolgreich', description: 'Bitte bestätige deine E-Mail-Adresse.' });
      setView('login');
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const result = await resetPassword(email);
    setSubmitting(false);
    if (result.error) {
      toast({ title: 'Fehler', description: result.error, variant: 'destructive' });
    } else {
      toast({ title: 'E-Mail gesendet', description: 'Prüfe dein Postfach für den Passwort-Link.' });
      setView('login');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Klyma</h1>
          <p className="mt-1 text-sm text-muted-foreground">Fahrzeugbestand verwalten</p>
        </div>

        <Card>
          {view === 'login' && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Anmelden</CardTitle>
                <CardDescription>Mit deinem Konto anmelden</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleLogin} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="name@firma.de" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password">Passwort</Label>
                    <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••" />
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? 'Anmelden…' : 'Anmelden'}
                  </Button>
                </form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                  <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">oder</span></div>
                </div>

                <Button variant="outline" className="w-full" onClick={signInWithGoogle}>
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                  Mit Google anmelden
                </Button>

                <div className="flex items-center justify-between text-xs">
                  <button onClick={() => setView('forgot')} className="text-muted-foreground hover:text-foreground hover:underline">Passwort vergessen?</button>
                  <button onClick={() => setView('register')} className="text-muted-foreground hover:text-foreground hover:underline">Registrieren</button>
                </div>
              </CardContent>
            </>
          )}

          {view === 'register' && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Registrieren</CardTitle>
                <CardDescription>Neues Konto erstellen</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleRegister} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Name</Label>
                    <Input value={name} onChange={e => setName(e.target.value)} required placeholder="Vor- und Nachname" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>E-Mail</Label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="name@firma.de" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Passwort</Label>
                    <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Mindestens 6 Zeichen" minLength={6} />
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? 'Registrieren…' : 'Registrieren'}
                  </Button>
                </form>
                <button onClick={() => setView('login')} className="text-xs text-muted-foreground hover:text-foreground hover:underline w-full text-center">Zurück zur Anmeldung</button>
              </CardContent>
            </>
          )}

          {view === 'forgot' && (
            <>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Passwort zurücksetzen</CardTitle>
                <CardDescription>Wir senden dir einen Link per E-Mail</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={handleForgot} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>E-Mail</Label>
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="name@firma.de" />
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? 'Senden…' : 'Link senden'}
                  </Button>
                </form>
                <button onClick={() => setView('login')} className="text-xs text-muted-foreground hover:text-foreground hover:underline w-full text-center">Zurück zur Anmeldung</button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
